import React from "react";
import { Box, Typography, Stack } from "@mui/material";
import colors from "../../../styles/colors";
import { COLORS } from "../../../styles/uiConstants";
import EmptyIllustration from "./EmptyIllustration";
import SearchIllustration from "./SearchIllustration";

const VARIANT_CONFIG = {
  card: {
    padding: { xs: 4, md: 6 },
    iconSize: 64,
    titleVariant: "h6",
    descriptionVariant: "body2",
    gap: 2,
  },
  inline: {
    padding: { xs: 2, md: 3 },
    iconSize: 32,
    titleVariant: "subtitle1",
    descriptionVariant: "body2",
    gap: 1.5,
  },
  compact: {
    padding: { xs: 1.5, md: 2 },
    iconSize: 28,
    titleVariant: "subtitle2",
    descriptionVariant: "caption",
    gap: 1,
  },
};

/**
 * Boş durum.
 * variant: "card" | "inline" | "compact" | "illustrated"
 * "illustrated" (ve küçük alanlar için "illustrated-sm"): halkalar üzerinde yüzen dosya
 * kutucukları + ortada `icon`. Listeler/tablolar ve drawer'lardaki "bulunamadı/yok"
 * durumları için standart görünüm.
 * illustration (yalnız illustrated varyantlarında, opt-in): "files" (varsayılan) |
 * "search" — dosyaların üstünde dolaşan hareketli büyüteç (tablo boş durumu).
 */
export default function EmptyState({
  icon,
  title,
  description,
  action,
  variant = "card",
  illustration = "files",
  sx = {},
}) {
  if (variant === "illustrated" || variant === "illustrated-sm") {
    const small = variant === "illustrated-sm";
    return (
      <Box
        role="status"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          px: 2,
          py: small ? 2 : 3,
          ...sx,
        }}
      >
        {illustration === "search" ? (
          <SearchIllustration icon={icon} size={small ? "sm" : "md"} />
        ) : (
          <EmptyIllustration icon={icon} size={small ? "sm" : "md"} />
        )}
        <Typography sx={{ mt: small ? 0.5 : 1, fontSize: small ? "0.9375rem" : "1.0625rem", fontWeight: 600, color: COLORS.slate800 }}>
          {title}
        </Typography>
        {description ? (
          <Typography sx={{ mt: 0.75, fontSize: small ? "0.8125rem" : "0.875rem", color: COLORS.slate500, maxWidth: 380, lineHeight: 1.6 }}>
            {description}
          </Typography>
        ) : null}
        {action ? <Box sx={{ mt: 1.5 }}>{action}</Box> : null}
      </Box>
    );
  }

  const config = VARIANT_CONFIG[variant] || VARIANT_CONFIG.card;

  return (
    <Box
      role="status"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: config.padding,
        px: config.padding,
        color: COLORS.slate600,
        ...sx,
      }}
    >
      <Stack spacing={config.gap} alignItems="center">
        {icon ? (
          <Box
            sx={{
              width: config.iconSize,
              height: config.iconSize,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: colors.primary,
              "& svg": { fontSize: config.iconSize },
            }}
          >
            {icon}
          </Box>
        ) : null}
        <Typography
          variant={config.titleVariant}
          sx={{ fontWeight: 600, color: COLORS.slate700 }}
        >
          {title}
        </Typography>
        {description ? (
          <Typography
            variant={config.descriptionVariant}
            sx={{ color: COLORS.slate500, maxWidth: 420 }}
          >
            {description}
          </Typography>
        ) : null}
        {action ? <Box sx={{ mt: 1 }}>{action}</Box> : null}
      </Stack>
    </Box>
  );
}
